import { toast } from 'sonner';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import api from '../api';

function Profile() {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const isCa = i18n.language === 'ca';

  useEffect(() => {
    if (user?.role === 'prestamista') fetchProfile();
  }, [user]);

  const fetchProfile = async () => {
    try {
      const res = await api.get('/api/profile');
      setProfileData(res.data);
      setFormData({
        name: res.data.user?.name || '',
        surname: res.data.user?.surname || '',
        phone: res.data.profile?.phone || '',
        iban: res.data.profile?.iban || '',
        address_full: res.data.profile?.address_full || '',
        doc_type: res.data.profile?.doc_type || 'DNI',
        doc_number: res.data.profile?.doc_number || '',
      });
    } catch (e) { console.error(e); }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api.put('/api/profile', formData);
      toast.success(isCa ? 'Perfil actualitzat' : 'Perfil actualizado');
      fetchProfile();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally { setSaving(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error(t('profile.passwordsDontMatch')); return; }
    if (newPassword.length < 6) { toast.error(t('profile.minChars')); return; }
    setLoading(true);
    try {
      await api.post('/api/auth/change-password', { old_password: oldPassword, new_password: newPassword });
      toast.success(t('profile.passwordUpdated'));
      setOldPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally { setLoading(false); }
  };

  const inputStyle = {
    padding: '0.75rem 1rem', fontSize: '0.9rem',
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '10px', color: 'white', width: '100%', boxSizing: 'border-box', outline: 'none'
  };

  const cardStyle = {
    background: 'linear-gradient(135deg, #1a1a1a 0%, #141414 100%)',
    border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '1.5rem'
  };

  return (
    <div className="main-layout">
      <Sidebar />
      <div className="content-area">
        <TopBar title={t('profile.title')} />
        <main style={{ padding: '1.5rem', maxWidth: '800px', margin: '0 auto' }} className="fade-in">
          
          {/* Info Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
            {[
              { label: t('profile.userName'), value: `${user?.name} ${user?.surname}`, symbol: 'U', bgColor: 'rgba(96,165,250,0.15)', iconColor: '#60a5fa' },
              { label: t('profile.userEmail'), value: user?.email, symbol: '@', bgColor: 'rgba(52,211,153,0.15)', iconColor: '#34d399' },
              { label: t('profile.userType'), value: user?.role === 'admin' ? t('profile.administrator') : t('profile.lender'), symbol: '#', bgColor: 'rgba(168,85,247,0.15)', iconColor: '#a855f7' },
              { label: t('profile.userLanguage'), value: user?.language_pref?.toUpperCase(), symbol: user?.language_pref === 'es' ? 'ES' : 'CA', bgColor: 'rgba(251,146,60,0.15)', iconColor: '#fb923c' }
            ].map(({label, value, symbol, bgColor, iconColor}) => (
              <div key={label} style={{
                ...cardStyle, padding: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem'
              }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', color: iconColor, fontWeight: '700', fontFamily: 'monospace', flexShrink: 0 }}>
                  {symbol}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.2rem' }}>{label}</p>
                  <p style={{ fontSize: '0.85rem', fontWeight: '600', color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Editable Personal Data (Lenders only) */}
          {user?.role === 'prestamista' && profileData && (
            <div style={{ ...cardStyle, marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.05rem', fontWeight: '600', color: 'white', marginBottom: '1.25rem' }}>
                {isCa ? 'Les meves dades' : 'Mis datos'}
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.3rem' }}>{isCa ? 'Nom' : 'Nombre'}</label>
                  <input style={inputStyle} value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} data-testid="profile-name" />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.3rem' }}>{isCa ? 'Cognoms' : 'Apellidos'}</label>
                  <input style={inputStyle} value={formData.surname} onChange={(e) => setFormData({...formData, surname: e.target.value})} data-testid="profile-surname" />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.3rem' }}>{isCa ? 'Telèfon' : 'Teléfono'}</label>
                  <input style={inputStyle} value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} data-testid="profile-phone" />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.3rem' }}>{formData.doc_type || 'DNI'}</label>
                  <input style={inputStyle} value={formData.doc_number} onChange={(e) => setFormData({...formData, doc_number: e.target.value})} data-testid="profile-doc" />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.3rem' }}>{isCa ? 'Adreça' : 'Dirección'}</label>
                  <input style={inputStyle} value={formData.address_full} onChange={(e) => setFormData({...formData, address_full: e.target.value})} data-testid="profile-address" />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.3rem' }}>IBAN</label>
                  <input style={{...inputStyle, fontFamily: 'monospace', letterSpacing: '0.05em'}} value={formData.iban} onChange={(e) => setFormData({...formData, iban: e.target.value})} placeholder="ES00 0000 0000 0000 0000 0000" data-testid="profile-iban" />
                </div>
              </div>
              <button onClick={handleSaveProfile} disabled={saving} data-testid="save-profile-btn" style={{
                marginTop: '1.25rem', padding: '0.75rem 2rem', background: 'linear-gradient(135deg, #103766, #1a5276)',
                color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem'
              }}>
                {saving ? '...' : (isCa ? 'Desar canvis' : 'Guardar cambios')}
              </button>
            </div>
          )}

          {/* Password Form */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: '1.05rem', fontWeight: '600', color: 'white', marginBottom: '1.25rem' }}>
              {t('profile.changePassword')}
            </h3>
            <form onSubmit={handleChangePassword} style={{ display: 'grid', gap: '1rem' }}>
              <input type="password" required value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} placeholder={t('profile.currentPassword')} style={inputStyle} />
              <input type="password" required value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder={t('profile.newPassword')} style={inputStyle} />
              <input type="password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder={t('profile.confirmPassword')} style={inputStyle} />
              <button type="submit" disabled={loading} data-testid="change-password-btn" style={{
                padding: '0.75rem', background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
                color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem'
              }}>
                {loading ? '...' : t('profile.changePassword')}
              </button>
            </form>
          </div>

          {/* Admin: Reset Database */}
          {user?.role === 'admin' && (
            <div className="dark-card" style={{ padding: '1.5rem', marginTop: '2rem', border: '2px solid rgba(239,68,68,0.3)' }}>
              <h3 style={{ color: '#ef4444', fontSize: '1.1rem', fontWeight: '700', marginBottom: '0.5rem' }}>
                {t('common.locale') === 'ca-ES' ? 'Zona Perillosa' : 'Zona Peligrosa'}
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', marginBottom: '1rem' }}>
                {t('common.locale') === 'ca-ES' 
                  ? 'Neteja tota la base de dades. Només es conservaran l\'admin i joan@vivendius.cat.'
                  : 'Limpia toda la base de datos. Solo se conservarán el admin y joan@vivendius.cat.'}
              </p>
              
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', cursor: 'pointer' }}>
                <input type="checkbox" id="clean-cdn-checkbox" data-testid="clean-cdn-checkbox" style={{ width: '18px', height: '18px', accentColor: '#ef4444', cursor: 'pointer' }} />
                <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>
                  {t('common.locale') === 'ca-ES' 
                    ? 'També netejar fitxers del CDN (contractes, documents, firmes)'
                    : 'También limpiar archivos del CDN (contratos, documentos, firmas)'}
                </span>
              </label>
              
              <button data-testid="reset-database-btn" onClick={async () => {
                const cleanCdn = document.getElementById('clean-cdn-checkbox')?.checked || false;
                const cdnMsg = cleanCdn 
                  ? (t('common.locale') === 'ca-ES' ? '\n\nTAMBÉ S\'ELIMINARAN TOTS ELS FITXERS DEL CDN!' : '\n\nTAMBIÉN SE ELIMINARÁN TODOS LOS ARCHIVOS DEL CDN!')
                  : '';
                const msg = (t('common.locale') === 'ca-ES'
                  ? 'Això eliminarà TOTS els prestamistes, préstecs, contractes i documents.' + cdnMsg + '\n\nEscriu "NETEJAR" per confirmar:'
                  : 'Esto eliminará TODOS los prestamistas, préstamos, contratos y documentos.' + cdnMsg + '\n\nEscribe "LIMPIAR" para confirmar:');
                const confirmText = prompt(msg);
                if (confirmText !== 'NETEJAR' && confirmText !== 'LIMPIAR') return;
                try {
                  const resp = await api.post('/api/admin/reset-database', { clean_cdn: cleanCdn });
                  const summary = resp.data.deleted;
                  let resultMsg = Object.entries(summary).map(([k, v]) => `${k}: ${v}`).join('\n');
                  if (resp.data.cdn_deleted && Object.keys(resp.data.cdn_deleted).length > 0) {
                    resultMsg += '\n\nCDN:\n' + Object.entries(resp.data.cdn_deleted).map(([k, v]) => `${k}: ${v} fitxers`).join('\n');
                  }
                  alert(resultMsg);
                  window.location.reload();
                } catch (err) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
              }} style={{
                padding: '0.75rem 1.5rem', background: 'rgba(239,68,68,0.15)', color: '#ef4444',
                border: '1.5px solid rgba(239,68,68,0.4)', borderRadius: '10px', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer'
              }}>
                {t('common.locale') === 'ca-ES' ? 'Netejar Base de Dades' : 'Limpiar Base de Datos'}
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default Profile;
